import React, { useEffect, useState } from 'react';
import '../styles/UserProfile.css';
import Service from '../services/Service';
import showToast from '../../../../Shared/Utils/ToastNotification';

const UserProfile = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    address: '',
    city: '',
    zip_code: '',
  });

  const [enable2FA, setEnable2FA] = useState(false); // 🔹 Two-Factor Auth flag
  const [isStaff, setIsStaff] = useState(false); // 🔹 check if user is admin/staff
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');

  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_new_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old_password: false,
    new_password: false,
    confirm_new_password: false,
  });
  // 🟢 Fetch profile on mount
  const getUserProfile = async () => {
    try {
      const response = await Service.userProfile();
      const data = response.data;
      setFormData(data);
      setEnable2FA(data.enable_2fa || false); // set initial 2FA state
      // setIsStaff(data.is_staff === true); // hide 2FA if user is staff/admin
      setIsStaff(false); // always false so 2FA always shows
    } catch (error) {
      console.error(error);
      showToast('error', 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  // 🟡 Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🟢 Save profile changes (include 2FA only if not staff/admin)
  const handleSave = async () => {
    try {
      setSaving(true);

      const updatedData = { ...formData };
      if (!isStaff) {
        updatedData.enable_2fa = enable2FA;
      }

      if (profileImage) {
        const formDataToSend = new FormData();

        Object.entries(updatedData).forEach(([key, value]) => {
          // 🟢 Skip the URL field, backend doesn't need it
          if (key !== 'profile_picture_url' && value !== null && value !== undefined) {
            formDataToSend.append(key, value);
          }
        });

        formDataToSend.append('profile_picture_upload', profileImage);

        await Service.updateUserProfile(formDataToSend);
      } else {
        await Service.updateUserProfile(updatedData);
      }

      showToast('success', 'Profile updated successfully!');
      await getUserProfile(); // 🔄 Fetch updated data (should reflect new image)
    } catch (error) {
      console.error('❌ Error updating profile:', error);

      // If backend returns validation or 400/500 errors
      if (error.response) {
        console.error('❌ Response data:', error.response.data);
        console.error('❌ Response status:', error.response.status);
      }

      showToast('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // 🟣 Password change handlers
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async () => {
    const { old_password, new_password, confirm_new_password } = passwordData;

    if (!old_password || !new_password || !confirm_new_password) {
      return showToast('error', 'Please fill all fields');
    }

    if (new_password !== confirm_new_password) {
      return showToast('error', 'New passwords do not match');
    }

    try {
      setChangingPassword(true);
      await Service.changePasswordUser(passwordData);
      showToast('success', 'Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ old_password: '', new_password: '', confirm_new_password: '' });
    } catch (error) {
      console.error(error);
      showToast('error', 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // 🟠 Toggle 2FA status
  const toggle2FA = () => {
    setEnable2FA((prev) => !prev);
  };

  if (loading) return <div className="profile-loading">Loading profile...</div>;

  return (
    <div className="profile-wrapper">
      <div className="profile-box">
        <h2 className="profile-heading">Edit Your Profile</h2>
        <div className="profile-image-container">
          <label htmlFor="profile-upload" className="profile-image-wrapper">
            <img src={previewImage || formData.profile_picture_url || '/default-avatar.png'} alt="Profile" className="profile-image" />
            <div className="camera-icon">📷</div>
          </label>
          <input type="file" id="profile-upload" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
        </div>

        <div className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input name="first_name" value={formData.first_name} onChange={handleChange} type="text" />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="last_name" value={formData.last_name} onChange={handleChange} type="text" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input name="email" value={formData.email} type="email" disabled />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input name="phone_number" value={formData.phone_number} onChange={handleChange} type="text" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Address</label>
              <input name="address" value={formData.address} onChange={handleChange} type="text" />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="city" value={formData.city} onChange={handleChange} type="text" />
            </div>
          </div>

          <div className="form-row single">
            <div className="form-group">
              <label>Zip Code</label>
              <input name="zip_code" value={formData.zip_code} onChange={handleChange} type="text" />
            </div>
          </div>

          {/* 🔒 Two-Factor Auth Section - only for non-staff */}
          {/* {!isStaff && (
            <div className="form-row single">
              <div className="form-group twofa-group">
                <label style={{ color: 'black', display: 'inline', fontWeight: '600' }}>Two-Factor Authentication (OTP)</label>
                <div className={`twofa-status ${enable2FA ? 'enabled' : 'disabled'}`} onClick={toggle2FA} title="Click to toggle">
                  {enable2FA ? '✅ Enabled' : '❌ Disabled'}
                </div>
                <small className="twofa-note">Click the status to toggle, then click Save Changes.</small>
              </div>
            </div>
          )} */}
          <div className="form-row single">
            <div className="form-group twofa-group">
              <label style={{ display: 'inline', fontWeight: '600' }}>Two-Factor Authentication (OTP)</label>

              <div className={`twofa-status ${enable2FA ? 'enabled' : 'disabled'}`} onClick={toggle2FA}>
                {enable2FA ? '✅ Enabled' : '❌ Disabled'}
              </div>

              <small className="twofa-note">Click the status to toggle, then click Save Changes.</small>
            </div>
          </div>

          <div className="button-group">
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="change-pass-btn" onClick={() => setShowPasswordModal(true)}>
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* 🔒 Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Change Password</h3>

            <div className="form-group">
              <label style={{ color: 'black' }}>Old Password</label>
              <div className="password-input">
                <input
                  type={showPassword.old_password ? 'text' : 'password'}
                  name="old_password"
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                />
                <span className="eye-icon" onClick={() => setShowPassword((prev) => ({ ...prev, old_password: !prev.old_password }))}>
                  {showPassword.old_password ? '🙈' : '👁️'}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label style={{ color: 'black' }}>New Password</label>
              <div className="password-input">
                <input
                  type={showPassword.new_password ? 'text' : 'password'}
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                />
                <span className="eye-icon" onClick={() => setShowPassword((prev) => ({ ...prev, new_password: !prev.new_password }))}>
                  {showPassword.new_password ? '🙈' : '👁️'}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label style={{ color: 'black' }}>Confirm New Password</label>
              <div className="password-input">
                <input
                  type={showPassword.confirm_new_password ? 'text' : 'password'}
                  name="confirm_new_password"
                  value={passwordData.confirm_new_password}
                  onChange={handlePasswordChange}
                />
                <span className="eye-icon" onClick={() => setShowPassword((prev) => ({ ...prev, confirm_new_password: !prev.confirm_new_password }))}>
                  {showPassword.confirm_new_password ? '🙈' : '👁️'}
                </span>
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </button>
              <button className="change-btn" onClick={handlePasswordSubmit} disabled={changingPassword}>
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
